import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Title,
} from '@common/components';
import { useSyncTagsQuery } from '@dashboard/api/syncMonitor';
import TagBadge, { CenterStatusBadge } from '@dashboard/components/SyncMonitor/TagBadge/TagBadge';
import classes from './RulesHelp.module.scss';

const TAG_SECTIONS = [
  { key: 'role', tags: ['active', 'new', 'historic', 'abandoned'] },
  { key: 'flag', tags: ['historic-sending'] },
  { key: 'freshness', tags: ['fresh', 'stale', 'never'] },
  { key: 'pf', tags: ['pf-fresh', 'pf-semifresh', 'pf-stale', 'pf-never'] },
];
const STATUSES = ['CREATED', 'ACTIVE', 'DISABLED'];
const ANOMALIES = [
  'historic_pf_fresh',
  'historic_fresh',
  'active_stale',
  'active_never',
  'new_stale',
  'disabled_fresh',
  'active_status_never',
];

/**
 * "Rules" button + modal that explains, with the thresholds the server is using right now, how every
 * tag, the center-status, the filters, the spans and the anomalies are calculated.
 */
export default function RulesHelp() {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });
  const [open, setOpen] = useState(false);
  const { data: resp } = useSyncTagsQuery();
  const rules = resp?.data?.rules ?? {};
  const anomalyCounts = resp?.data?.anomaly_counts ?? {};
  const values = {
    minDays: rules.fresh_min_days ?? 5,
    maxAgeDays: rules.fresh_max_age_days ?? 365,
    activeMinDays: rules.active_min_days ?? 5,
    newMaxDays: rules.new_max_days ?? 90,
    newQuietDays: rules.new_quiet_days ?? 30,
    pfFreshFrom: rules.pf_fresh_from ?? '',
    pfSemifreshDays: rules.pf_semifresh_days ?? 90,
  };

  return (
    <>
      <button
        type="button"
        className={classes.trigger}
        title={t('helpButtonHint')}
        onClick={() => setOpen(true)}
      >
        ? {t('helpButton')}
      </button>

      {open && (
        <Modal visible closeOnClickOutside onClose={() => setOpen(false)}>
          <ModalHeader>
            <Title>{t('helpTitle')}</Title>
          </ModalHeader>
          <ModalContent className={classes.content}>
            <p>{t('helpIntro')}</p>
            <p className={classes.note}>{t('helpReceivedDays')}</p>

            {TAG_SECTIONS.map((section) => (
              <section key={section.key} className={classes.section}>
                <h4>{t(`tagGroup_${section.key}`)}</h4>
                <p className={classes.note}>
                  {t(`helpGroup_${section.key}`, values)}
                </p>
                <dl className={classes.rules}>
                  {section.tags.map((tag) => (
                    <React.Fragment key={tag}>
                      <dt>
                        <TagBadge tag={tag} />
                      </dt>
                      <dd>{t(`tagHint_${tag}`, values)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>
            ))}

            <section className={classes.section}>
              <h4>{t('centerStatus')}</h4>
              <p className={classes.note}>{t('helpCenterStatus')}</p>
              <dl className={classes.rules}>
                {STATUSES.map((status) => (
                  <React.Fragment key={status}>
                    <dt>
                      <CenterStatusBadge status={status} />
                    </dt>
                    <dd>{t(`helpStatus_${status}`)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </section>

            <section className={classes.section}>
              <h4>{t('helpFiltersTitle')}</h4>
              <p>{t('helpFilters')}</p>
              <p className={classes.note}>{t('helpDefaults')}</p>
            </section>

            <section className={classes.section}>
              <h4>{t('helpSpanTitle')}</h4>
              <p>{t('helpSpan')}</p>
            </section>

            <section className={classes.section}>
              <h4>{t('helpAnomaliesTitle')}</h4>
              <p className={classes.note}>{t('helpAnomalies')}</p>
              <dl className={classes.rules}>
                {ANOMALIES.map((code) => (
                  <React.Fragment key={code}>
                    <dt className={classes.code}>
                      {t(`anomaly_${code}`)}
                      <span className={classes.count}>
                        {anomalyCounts[code] ?? 0}
                      </span>
                    </dt>
                    <dd>{t(`anomalyHint_${code}`, values)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </section>
          </ModalContent>
          <ModalFooter>
            <Button
              variant="outline"
              color="accent"
              onClick={() => setOpen(false)}
            >
              {t('helpClose')}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}
